import React from 'react';

type EmbellishmentItem = {
    topEdge: { active: boolean };
    bottomEdge: { active: boolean };
    leadReturnEdge: { active: boolean };
    totalStrips: number;
    draperyOrderFabric?: { name: string };
};

type TrimItem = EmbellishmentItem & {
    topEdge: { shade: boolean; valance: boolean; };
    bottomEdge: { shade: boolean; valance: boolean; };
    leadReturnEdge: { shade: boolean; valance: boolean; };
};

interface AdvancedEmbellishmentDetailsProps {
    salesDescriptionSettingsMapped: { id: string; isEnabled: boolean }[];
    data: {
        orderType: string | undefined;
        embellishmentsList: {
            checked: boolean;
            title: string;
            stateName?: string;
        }[];
        bandings: any[];
        cordings: any[];
        flanges: any[];
        trims: any[];
        othersEmbellishments: { name: string }[];
    };
}

const getEmbellishmentName = (type: string, item: EmbellishmentItem): string => {
    const fabricName = item && item.draperyOrderFabric && item.draperyOrderFabric.name ? item.draperyOrderFabric.name : '';
    switch (type) {
        case "Cording":
            if (fabricName.includes("WELT CORDING FABRIC")) return "Welt Cording";
            if (fabricName.includes("CORDING (PRE-MADE)")) return "Cording (Pre-Made)";
            return "Cording";
        case "Flange":
            if (fabricName.includes("FLANGE FABRIC")) return "Flange from Fabric";
            if (fabricName.includes("FLANGE (PRE-MADE)")) return "Flange (Pre-Made)";
            return "Flange";
        case "Trim":
            if (fabricName.includes("TRIM FABRIC")) return "Trim from Fabric";
            if (fabricName.includes("TRIM (PRE-MADE)")) return "Trim (Pre-Made)";
            return "Trim";
        default:
            return type;
    }
};

const formatDraperyLocation = (item: EmbellishmentItem, orderType: string | undefined): string => {
    const parts = [];
    if (item.topEdge.active) parts.push("on Top Edge");
    if (item.bottomEdge.active) parts.push(item.topEdge.active ? "Bottom Edge" : "on Bottom Edge");
    
    const hasVertical = item.leadReturnEdge.active && (item.totalStrips > 0 || orderType === "roman-shade-order");
    if (hasVertical) {
        const stripCount = item.totalStrips ? `${item.totalStrips} ` : "";
        const plural = item.totalStrips > 1 ? "s" : "";
        const prefix = parts.length > 0 ? "and " : "on ";
        parts.push(`${prefix}${stripCount}Vertical Edge${plural}`);
    }

    return parts.length > 0 ? ` ${parts.join(', ').replace(/,([^,]*)$/, ' and$1')}.` : ".";
};

const formatTrimLocation = (trim: TrimItem): string => {
    const shadeParts = [];
    if (trim.topEdge.shade) shadeParts.push("top edge");
    if (trim.leadReturnEdge.shade) shadeParts.push("sides");
    if (trim.bottomEdge.shade) shadeParts.push("bottom edge");

    const valanceParts = [];
    if (trim.topEdge.valance) valanceParts.push("top edge");
    if (trim.leadReturnEdge.valance) valanceParts.push("sides");
    if (trim.bottomEdge.valance) valanceParts.push("bottom edge");
    
    let result = "";
    if (shadeParts.length > 0) {
        result += ` on ${shadeParts.join(', ').replace(/,([^,]*)$/, ' and$1')} of Shade`;
    }
    if (valanceParts.length > 0) {
        result += `${result ? ' and' : ''} on ${valanceParts.join(', ').replace(/,([^,]*)$/, ' and$1')} of Valance`;
    }

    return result ? `${result}.` : ".";
};

export const RomanEmbellishmentDetails: React.FC<AdvancedEmbellishmentDetailsProps> = ({
    salesDescriptionSettingsMapped,
    data,
}) => {
    // const isEnabled = salesDescriptionSettingsMapped.some(
    //     (item) => item.id === "salesembellishmentsselected" && item.isEnabled
    // );

    // // if (!isEnabled) {
    // //     return null;
    // // }

    const { embellishmentsList, orderType } = data;
    const hasCheckedEmbellishments = embellishmentsList.some(e => e.checked);
    const isTypeChecked = (title: string) => embellishmentsList.some(e => e.checked && e.title === title);
    const isOtherChecked = () => embellishmentsList.some(e => e.checked && e.stateName === "othersEmbellishments");

    const renderEmbellishment = (type: string, item: EmbellishmentItem | TrimItem, index: number) => {
        const name = getEmbellishmentName(type, item);
        let location = "";

        if (type === "Trim" && orderType === "roman-shade-order") {
            location = formatTrimLocation(item as TrimItem);
        } else {
            location = formatDraperyLocation(item, orderType);
        }

        return <span key={`${type}-${index}`}> {name}{location}</span>;
    };
    
    return (
        <div>
            <span className="nonBold">Embellishments:</span>
            {!hasCheckedEmbellishments ? (
                <span> None</span>
            ) : (
                <span>
                    {isTypeChecked("Banding") && data.bandings.map((item, i) => renderEmbellishment("Banding", item, i))}
                    {isTypeChecked("Cording") && data.cordings.map((item, i) => renderEmbellishment("Cording", item, i))}
                    {isTypeChecked("Flange") && data.flanges.map((item, i) => renderEmbellishment("Flange", item, i))}
                    {isTypeChecked("Trim") && data.trims.map((item, i) => renderEmbellishment("Trim", item, i))}
                    {isOtherChecked() && data.othersEmbellishments.some(o => !!o.name) && (
                        <span> {data.othersEmbellishments.map(o => o.name).filter(Boolean).join(", ")}.</span>
                    )}
                </span>
            )}
        </div>
    );
};